// Inspecteur → Bewijsmiddelen / Bewijscontainers (evidence register) — FRONTEND-BLUEPRINT §3.1 / §4. Re-skin
// on the shadcn design system: PageHeader + DataTable of evidence containers, reusing
// api.listBewijscontainers()/createBewijscontainer() + the real R2 file methods (no new endpoints). A
// container is a named evidence requirement; its files carry the bytes (upload/download/delete via the
// tenant-worker proxy, ≤25 MB, auth + RLS enforced server-side).
import { useState, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TENANT_PATHS } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { Can } from "../../lib/rbac";
import { useI18n } from "../../lib/i18n";
import { humanizeEnum } from "../../lib/enum-labels";
import type { Column } from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  RegisterScreen, CreateDialog, DeactivateButton, TagBadge, errText, type Row, type FormField,
} from "./_shared";

const EVIDENCE_KINDS = ["document", "screenshot", "configuration_export", "log_excerpt", "external_link", "human_attestation"];

const CREATE_FIELDS: FormField[] = [
  { key: "name", label: "Naam", required: true },
  { key: "anchorKind", label: "Anker (soort, bv. verwerking)", required: true, placeholder: "verwerking" },
  { key: "anchorId", label: "Anker-id (uuid)", required: true },
  { key: "description", label: "Omschrijving", type: "textarea" },
];

export function InspectorEvidenceRoute() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["bewijscontainers"] });

  const columns: Column<Row>[] = [
    { key: "name", header: "Naam", render: (r) => <span className="font-medium">{String(r.name ?? "—")}</span> },
    { key: "anchorKind", header: "Anker", render: (r) => <TagBadge value={r.anchorKind} /> },
    { key: "anchorId", header: "Anker-id", render: (r) => <span className="font-mono text-xs text-muted-foreground">{String(r.anchorId ?? "—")}</span> },
  ];

  return (
    <RegisterScreen
      title={t("nav.evidence")}
      description={t("inspector.evidence.sub")}
      help={t("help.evidence")}
      queryKey="bewijscontainers"
      listFn={() => api.listBewijscontainers()}
      columns={columns}
      empty={t("inspector.evidence.empty")}
      readPerm="bewijscontainer.read"
      onRowClick={(r) => { if (r.id) void navigate({ to: "/bewijscontainers/$id", params: { id: String(r.id) } }); }}
      actions={
        <Can perm="bewijscontainer.write">
          <CreateDialog
            title={t("inspector.evidence.new")}
            fields={CREATE_FIELDS}
            initial={{ anchorKind: "verwerking" }}
            onCreate={(i) => api.createBewijscontainer(i as unknown as Parameters<typeof api.createBewijscontainer>[0])}
            onDone={invalidate}
          />
        </Can>
      }
      rowActions={(r) => {
        const id = String(r.id);
        return (
          <span className="inline-flex items-center gap-1">
            <FilesDialog bcId={id} name={String(r.name ?? "")} />
            <Can perm="bewijscontainer.delete"><DeactivateButton basePath={TENANT_PATHS.bewijscontainers} id={id} onDone={invalidate} /></Can>
          </span>
        );
      }}
    />
  );
}

/** Files manager Dialog for one evidence container — list + upload/download/delete real bytes. */
function FilesDialog({ bcId, name }: { bcId: string; name: string }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["evidence-files", bcId] });
  const { data } = useQuery({ queryKey: ["evidence-files", bcId], queryFn: () => api.listEvidenceFiles(bcId), enabled: open });
  const files = (data ?? []) as Row[];
  const [kind, setKind] = useState("document");
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: (file: File) => api.uploadEvidenceFile(bcId, file, kind),
    onSuccess: () => { if (fileRef.current) fileRef.current.value = ""; invalidate(); },
  });
  const download = useMutation({
    mutationFn: async (f: Row) => {
      const blob = await api.downloadEvidenceFile(bcId, String(f.id));
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = String(f.fileName ?? "evidence");
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    },
  });
  const del = useMutation({ mutationFn: (id: string) => api.deleteEvidenceFile(bcId, id), onSuccess: invalidate });
  const fmtSize = (b: unknown) => { const n = Number(b); return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n >= 1024 ? `${(n / 1024).toFixed(0)} KB` : `${n} B`; };
  const selectCls = "h-7 rounded-2xl border border-transparent bg-input/50 px-2.5 text-xs outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="xs">{t("ev.files")}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("ev.files")}{name ? ` — ${name}` : ""}</DialogTitle>
          <DialogDescription>{t("ev.maxSize")}</DialogDescription>
        </DialogHeader>
        <Can perm="bewijscontainer.upload">
          <div className="flex flex-wrap items-center gap-2">
            <select value={kind} onChange={(e) => setKind(e.target.value)} className={selectCls} aria-label={t("ev.upload")}>
              {EVIDENCE_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <Input ref={fileRef} type="file" className="h-7 w-auto text-xs" onChange={(e) => { const file = e.target.files?.[0]; if (file) upload.mutate(file); }} />
            {upload.isPending && <span className="text-xs text-muted-foreground">{t("ev.uploading")}</span>}
          </div>
        </Can>
        {upload.isError && <p className="text-sm text-destructive">{errText(upload.error)}</p>}
        {files.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("ev.none")}</p>
        ) : (
          <ul className="divide-y">
            {files.map((f) => (
              <li key={String(f.id)} className="flex items-center gap-2 py-2 text-sm">
                <span className="min-w-0 truncate font-medium">{String(f.fileName)}</span>
                <Badge variant="secondary" className="shrink-0">{humanizeEnum(f.evidenceKind)}</Badge>
                <span className="text-xs text-muted-foreground">{fmtSize(f.sizeBytes)}</span>
                <span className="ml-auto inline-flex items-center gap-1">
                  <Button variant="ghost" size="xs" disabled={download.isPending} onClick={() => download.mutate(f)}>{t("ev.download")}</Button>
                  <Can perm="bewijscontainer.delete"><Button variant="ghost" size="xs" className="text-destructive" disabled={del.isPending} onClick={() => del.mutate(String(f.id))}>{t("crud.deactivate")}</Button></Can>
                </span>
              </li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>{t("crud.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
